
/**
 * Calculates totals for a subtask, including material, subcontractor, and labor costs, sales, and profits.
 *
 * @param {Object} params
 * @param {Object} params.subtask - The subtask object containing arrays of material, subcontractor, and labor items.
 * @param {number|string} params.profitMargin - The profit margin value (percentage or fixed amount) to apply if sale amount is locked.
 * @param {string} params.profitMarginType - Type of profit margin: 'PERCENTAGE' or 'FIXED'.
 * @param {boolean} [params.isForm=false] - If true, expects form-style data structure for labor items.
 *
 * @returns {Object} subtask_total - Totals for material, subcontractor, labor, and overall profit, subtotal, forecast, and total.
 *
 * Use case: Used to calculate and summarize all cost, sale, and profit data for a subtask, including handling locked sale amounts and different profit margin types.
 */

import { cloneDeep, isString } from 'lodash';
import { Decimal, toDecimal, roundValue } from '../decimal-utils';

const { default: idx } = require('idx');

export const subTaskCalculation = ({ subtask, profitMargin, profitMarginType, isForm = false }) => {
    // Initialize totals for each category and overall using Decimal
    let subtask_total = {
        material: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0)
        },
        subcontractor: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0)
        },
        labor: {
            cost: new Decimal(0),
            sale: new Decimal(0),
            profit: new Decimal(0),
        },
        total_profit: new Decimal(0),
        subtotal: new Decimal(0),
        forecast: new Decimal(0),
        forecastHours: new Decimal(0),
        total: new Decimal(0)
    };
    try {
        // Extract arrays for each resource type
        let materials = subtask.material || [];
        let subcontractors = subtask.subcontractor || [];
        let labors = subtask.labor || [];

        // Calculate totals for all materials
        materials.map((material) => {
            // Parse and deep clone material object
            let _material = isString(material) ? JSON.parse(material) : material;
            _material = cloneDeep(_material);

            // Check if sale amount is locked (manual override)
            const isLocked = _material["saleAmountLock"] === true ||
                idx(_material, _ => _["saleAmountLock"].value) === true;

            // Get cost and quantity, defaulting to 0/1 if missing, using toDecimal
            // Get cost and quantity, defaulting to 0 if missing, using toDecimal
            const cost = toDecimal(
                idx(_material, _ => _['costPerItem']?.value) ??
                idx(_material, _ => _["cost"]) ?? 0
            );

            const qty = toDecimal(
                idx(_material, _ => _['qty']?.value) ??
                idx(_material, _ => _["qty"]) ?? 0
            );

            // Get sale amount, defaulting to 0 if missing
            let saleAmount = toDecimal(
                idx(_material, _ => _['saleAmount']?.value) ??
                idx(_material, _ => _["saleAmount"]) ?? 0
            );

            // If locked, recalculate sale amount using profit margin and round to 2 decimals
            if (isLocked) {
                const margin = toDecimal(profitMargin || 0);
                if (profitMarginType === "PERCENTAGE") {
                    saleAmount = cost.times(margin.dividedBy(100).plus(1)).toDecimalPlaces(2);
                } else if (profitMarginType === "FIXED") {
                    saleAmount = cost.plus(margin).toDecimalPlaces(2);
                }
            }

            // Calculate total cost and sale for this material using Decimal methods
            const matCost = cost.times(qty);
            let matSale;

            // If NOT locked (tender-rounded), use stored total to avoid rounding drift
            // This ensures frontend matches backend after tender rounding adjustments
            const storedTotal = toDecimal(
                idx(_material, _ => _['total']?.value) ??
                idx(_material, _ => _["total"]) ?? 0
            );
            if (!isLocked) {
                // Use stored total if it exists and is reasonable, otherwise recalculate
                // Force to 0 if quantity is 0 to avoid ghost totals
                matSale = (qty.greaterThan(0) && storedTotal.greaterThan(0)) ? storedTotal : saleAmount.times(qty);
            } else {
                matSale = saleAmount.times(qty);
            }

            // Add to running totals using Decimal arithmetic
            subtask_total.material.cost = subtask_total.material.cost.plus(matCost);
            subtask_total.material.sale = subtask_total.material.sale.plus(matSale);
            return JSON.stringify(_material);
        });

        // Calculate totals for all subcontractors
        subcontractors.map((subcontractor) => {
            // Parse and deep clone subcontractor object
            let _subcontractor = isString(subcontractor) ? JSON.parse(subcontractor) : subcontractor;
            _subcontractor = cloneDeep(_subcontractor);

            // Check if sale amount is locked
            const isLocked = _subcontractor["saleAmountLock"] === true ||
                idx(_subcontractor, _ => _["saleAmountLock"].value) === true;

            // Get cost and sale amount using toDecimal
            let cost = toDecimal(
                idx(_subcontractor, _ => _['Cost']?.value) ??
                idx(_subcontractor, _ => _["Cost"]) ?? 0
            );

            let saleAmount = toDecimal(
                idx(_subcontractor, _ => _['saleAmount']?.value) ??
                idx(_subcontractor, _ => _["saleAmount"]) ?? 0
            );

            // If locked, recalculate sale amount using profit margin and round to 2 decimals
            if (isLocked) {
                const margin = toDecimal(profitMargin || 0);
                if (profitMarginType === "PERCENTAGE") {
                    saleAmount = cost.times(margin.dividedBy(100).plus(1)).toDecimalPlaces(2);
                } else if (profitMarginType === "FIXED") {
                    saleAmount = cost.plus(margin).toDecimalPlaces(2);
                }
            } else {
                // If NOT locked (tender-rounded), use stored total to avoid rounding drift
                const storedTotal = toDecimal(
                    idx(_subcontractor, _ => _['total']?.value) ??
                    idx(_subcontractor, _ => _["total"]) ?? 0
                );
                if (storedTotal.greaterThan(0)) {
                    saleAmount = storedTotal;
                }
            }

            // Add to running totals using Decimal arithmetic
            subtask_total.subcontractor.cost = subtask_total.subcontractor.cost.plus(cost);
            subtask_total.subcontractor.sale = subtask_total.subcontractor.sale.plus(saleAmount);
            return JSON.stringify(_subcontractor);
        });

        // Calculate totals for all labors
        labors.map((labor) => {
            // Parse and deep clone labor object
            let _labor = isString(labor) ? JSON.parse(labor) : labor;
            _labor = cloneDeep(_labor);

            // Calculate labor cost and sale, using form fields if isForm is true
            const laborCost = toDecimal(
                isForm ? _labor['labourCost']?.value : _labor["averageCost"] || 0
            ).times(
                toDecimal(isForm ? _labor['qty']?.value : _labor["qty"] || 0)
            );

            const laborSale = toDecimal(
                isForm ? _labor['costPerItem']?.value : _labor["cost"] || 0
            ).times(
                toDecimal(isForm ? _labor['qty']?.value : _labor["qty"] || 0)
            );

            // Add to running totals using Decimal arithmetic
            subtask_total.labor.cost = subtask_total.labor.cost.plus(laborCost);
            subtask_total.labor.sale = subtask_total.labor.sale.plus(laborSale);
            // Only include in forecastHours if is_personnel is true/undefined (Source of Truth)
            const isPersonnel = isForm
                ? (_labor['is_personnel']?.value !== false && _labor['isPersonnel']?.value !== false)
                : (_labor["is_personnel"] !== false && _labor["isPersonnel"] !== false);

            if (isPersonnel) {
                subtask_total.forecastHours = subtask_total.forecastHours.plus(
                    toDecimal(isForm ? _labor['qty']?.value : _labor["qty"] || 0)
                );
            }
            return JSON.stringify(_labor);
        });

        // Calculate profits for each category using Decimal arithmetic
        subtask_total.material.profit = subtask_total.material.sale.minus(subtask_total.material.cost);
        subtask_total.subcontractor.profit = subtask_total.subcontractor.sale.minus(subtask_total.subcontractor.cost);
        subtask_total.labor.profit = subtask_total.labor.sale.minus(subtask_total.labor.cost);

        // Calculate total profit by summing all profit categories
        subtask_total.total_profit = subtask_total.material.profit
            .plus(subtask_total.subcontractor.profit)
            .plus(subtask_total.labor.profit);

        // Calculate subtotal (total cost) by summing all cost categories
        subtask_total.subtotal = subtask_total.material.cost
            .plus(subtask_total.subcontractor.cost)
            .plus(subtask_total.labor.cost);

        // Calculate total (total sale) by summing all sale categories
        subtask_total.total = subtask_total.material.sale
            .plus(subtask_total.subcontractor.sale)
            .plus(subtask_total.labor.sale);

        // Calculate forecasted cost if provided, using Decimal arithmetic
        subtask_total.forecast = toDecimal(subtask.materialForecastCost || 0)
            .plus(toDecimal(subtask.laborForecastCost || 0))
            .plus(toDecimal(subtask.subContractorForecastCost || 0));

        // Convert all Decimal values to numbers with 2-decimal rounding before returning
        return {
            material: {
                cost: roundValue(subtask_total.material.cost, 2),
                sale: roundValue(subtask_total.material.sale, 2),
                profit: roundValue(subtask_total.material.profit, 2)
            },
            subcontractor: {
                cost: roundValue(subtask_total.subcontractor.cost, 2),
                sale: roundValue(subtask_total.subcontractor.sale, 2),
                profit: roundValue(subtask_total.subcontractor.profit, 2)
            },
            labor: {
                cost: roundValue(subtask_total.labor.cost, 2),
                sale: roundValue(subtask_total.labor.sale, 2),
                profit: roundValue(subtask_total.labor.profit, 2)
            },
            total_profit: roundValue(subtask_total.total_profit, 2),
            subtotal: roundValue(subtask_total.subtotal, 2),
            forecast: roundValue(subtask_total.forecast, 2),
            forecastHours: roundValue(subtask_total.forecastHours, 2),
            total: roundValue(subtask_total.total, 2)
        };
    } catch (e) {
        // Log and return partial totals on error
        console.error('Error in subTaskCalculation:', e);
        // Ensure we return numbers, not Decimal objects in case of error
        return {
            material: {
                cost: roundValue(subtask_total.material.cost),
                sale: roundValue(subtask_total.material.sale),
                profit: roundValue(subtask_total.material.profit)
            },
            subcontractor: {
                cost: roundValue(subtask_total.subcontractor.cost),
                sale: roundValue(subtask_total.subcontractor.sale),
                profit: roundValue(subtask_total.subcontractor.profit)
            },
            labor: {
                cost: roundValue(subtask_total.labor.cost),
                sale: roundValue(subtask_total.labor.sale),
                profit: roundValue(subtask_total.labor.profit)
            },
            total_profit: roundValue(subtask_total.total_profit),
            subtotal: roundValue(subtask_total.subtotal),
            forecast: roundValue(subtask_total.forecast),
            forecastHours: roundValue(subtask_total.forecastHours || 0, 2),
            total: roundValue(subtask_total.total)
        };
    }
};


// Calculation for history values
// totalHistoryCost.materialCost +=
//     parseFloat(
//         idx(
//             task,
//             (_task) => _task.taskHistory[0].materialTotal_SaleAmount
//         )
//     ) || 0;
// totalHistoryCost.laborCost +=
//     parseFloat(
//         idx(
//             task,
//             (_task) => _task.taskHistory[0].laborTotal_SaleAmount
//         )
//     ) || 0;
// totalHistoryCost.profitMarginCost +=
//     parseFloat(
//         idx(task, (_task) => _task.taskHistory[0].profitMarginCost)
//     ) || 0;
// totalHistoryCost.subContractorCost +=
//     parseFloat(
//         idx(
//             task,
//             (_task) =>
//                 _task.taskHistory[0].subcontractorTotal_SaleAmount
//         )
//     ) || 0;
// totalHistoryCost.total =
//     totalHistoryCost.materialCost +
//     totalHistoryCost.laborCost +
//     totalHistoryCost.subContractorCost;